import React, { useState, useEffect, useRef } from "react";
import { AiOutlineUser, AiOutlineSetting, AiOutlineLogout, AiOutlineKey, AiOutlineMessage, AiOutlineSafety } from "react-icons/ai";
import { useAuth } from "react-oidc-context";
import UserDataModal from "./UserDataModal";
import useFonts from "../hooks/useFonts";
import useUser from "../hooks/useUser";

const UserMenu = ({ onLogout }) => {
  const auth = useAuth();
  const { hasRole } = useUser();
  const { fonts } = useFonts();
  const [isOpen, setIsOpen] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleShowUserData = () => {
    console.log("Datos del usuario OIDC:", auth.user);
    console.log("Usuario autenticado:", auth.isAuthenticated);
    console.log("Estado de carga:", auth.isLoading);
    setShowUserModal(true);
    setIsOpen(false); // Cierra el menú
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
  };

  const handleLogout = async () => {
    try {
      // Intentar cerrar sesión en el Proveedor OIDC
      await auth.signoutRedirect();
    } catch (error) {
      console.error('Error during signoutRedirect, falling back to local logout:', error);
      try {
        // Fallback: remover usuario local y refrescar a la raíz
        await auth.removeUser?.();
      } catch (e) {
        console.error('Error removing local user:', e);
      }
      window.location.href = '/';
    } finally {
      if (onLogout) onLogout();
      setIsOpen(false);
    }
  };

  // Cierra el menú si se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Obtener información del usuario para el avatar
  const getUserDisplayName = () => {
    if (!auth.user) return "U";
    const profile = auth.user.profile || {};

    // Intentar obtener iniciales de nombre + apellido
    if (profile.given_name && profile.family_name) {
      return (profile.given_name[0] + profile.family_name[0]).toUpperCase();
    }

    // Si no hay apellido, usar solo el nombre
    if (profile.given_name) {
      return profile.given_name[0].toUpperCase();
    }

    // Fallback a otros campos
    return profile.preferred_username?.[0] || profile.sub?.[0] || "U";
  };

  const getUserFullName = () => {
    if (!auth.user) return "Usuario";
    const profile = auth.user.profile || {};
    return profile.name || profile.preferred_username || profile.sub || "Usuario";
  };

  return (
    <div style={styles.container} ref={menuRef}>
      <style>
        {`
          .avatar-button:hover {
            background-color: #F3F4F6 !important;
          }
          .avatar-button.open .avatar {
            background-color: #00468B !important;
            color: #FFFFFF !important;
          }
          .menu-item:hover {
            background-color: #F3F4F6 !important;
          }
          .logout-item:hover {
            background-color: #FEF2F2 !important;
          }
        `}
      </style>
      <button
        onClick={toggleMenu}
        style={styles.avatarButton}
        className={`avatar-button ${isOpen ? 'open' : ''}`}
      >
        <div style={styles.avatar} className="avatar">
          {getUserDisplayName()}
        </div>
        <div style={styles.dropdownArrow}>
          ▼
        </div>
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          {/* Header con información del usuario */}
          <div style={styles.userHeader}>
            <div style={styles.userInfo}>
              <div style={styles.userName}>{getUserFullName()}</div>
              <div style={styles.userEmail}>
                {auth.user?.profile?.email || "usuario@ejemplo.com"}
              </div>
            </div>
          </div>

          {/* Separador */}
          <div style={styles.separator}></div>

          {/* Opciones del menú */}
          <div style={styles.menuItems}>
            <button
              style={styles.menuItem}
              className="menu-item"
              onClick={handleShowUserData}
            >
              <AiOutlineUser style={styles.menuIcon} />
              <span>Datos personales</span>
            </button>
            {hasRole('IMPORTER') && (
              <button
                style={styles.menuItem}
                className="menu-item"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = "/api-keys";
                }}
              >
                <AiOutlineKey style={styles.menuIcon} />
                <span>Mis API Keys</span>
              </button>
            )}
            {hasRole('IMPORTER') && (
              <button
                style={styles.menuItem}
                className="menu-item"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = "/integrations/telegram";
                }}
              >
                <AiOutlineMessage style={styles.menuIcon} />
                <span>Conectar Telegram</span>
              </button>
            )}
            {hasRole('SIGNER') && (
              <button
                style={styles.menuItem}
                className="menu-item"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = "/cliente-firma";
                }}
              >
                <AiOutlineSafety style={styles.menuIcon} />
                <span>Cliente local de firma</span>
              </button>
            )}

            {/*<button
              style={styles.menuItem}
              className="menu-item"
              onClick={() => {
                // Aquí podrías agregar configuración
                console.log("Configuración");
                setIsOpen(false);
              }}
            >
              <AiOutlineSetting style={styles.menuIcon} />
              <span>Configuración</span>
            </button>*/}
          </div>

          {/* Separador */}
          <div style={styles.separator}></div>

          {/* Logout */}
          <div style={styles.menuItems}>
            <button
              onClick={handleLogout}
              style={styles.logoutItem}
              className="logout-item"
            >
              <AiOutlineLogout style={styles.menuIcon} />
              <span>Salir</span>
            </button>
          </div>
        </div>
      )}

      <UserDataModal
        isOpen={showUserModal}
        onClose={handleCloseUserModal}
        userData={auth.user}
      />
    </div>
  );
};

const styles = {
  container: {
    position: "relative",
    display: "inline-block",
  },
  avatarButton: {
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "6px",
    transition: "background-color 0.2s ease",
  },
  avatarButtonHover: {
    backgroundColor: "#F3F4F6",
  },
  avatar: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    color: "#00468B",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "bold",
    marginRight: "8px",
    border: "2px solid #00468B",
  },
  dropdownArrow: {
    fontSize: "10px",
    color: "#666",
    transition: "transform 0.2s ease",
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    right: "0",
    marginTop: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #D1D5DB",
    borderRadius: "8px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    minWidth: "240px",
    zIndex: 1000,
    overflow: "hidden",
  },
  userHeader: {
    padding: "16px",
    backgroundColor: "#F8F9FA",
    borderBottom: "1px solid #E5E7EB",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
  },
  userName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#111827",
    marginBottom: "2px",
  },
  userEmail: {
    fontSize: "14px",
    color: "#6B7280",
  },
  separator: {
    height: "1px",
    backgroundColor: "#E5E7EB",
    margin: "0",
  },
  menuItems: {
    padding: "8px 0",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 16px",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontSize: "14px",
    color: "#374151",
    transition: "background-color 0.2s ease",
  },
  menuItemHover: {
    backgroundColor: "#F3F4F6",
  },
  menuIcon: {
    marginRight: "12px",
    fontSize: "16px",
    color: "#6B7280",
  },
  logoutItem: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 16px",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontSize: "14px",
    color: "#DC2626",
    transition: "background-color 0.2s ease",
  },
  logoutItemHover: {
    backgroundColor: "#FEF2F2",
  },
};

export default UserMenu;
